/**
 * Active-tenant store — the single source of truth for which team the signed-in
 * user is currently working inside.
 *
 * This is a PURE-TS module (no React) that persists the chosen team to
 * localStorage and exposes SYNCHRONOUS module-level accessors, mirroring the
 * old `getKbConfig` pattern. The synchronous getter matters because
 * {@link buildMetaInfo} (also pure TS) reads it on EVERY request to stamp the
 * active team's tenant bundle key into
 * `MetaInfo.executionResourceTypeBundle.storeBundleInfoIdentifier` — it cannot
 * await React state, so the value must be available synchronously.
 *
 * Tenant model:
 *  - The built-in "current workspace" team has bundleKey '' (empty). An empty
 *    bundle key leaves storeBundleInfoIdentifier UNSET, so requests resolve to
 *    the backend DEFAULT bundle — i.e. exactly the pre-multi-tenant behavior, so
 *    existing data stays visible.
 *  - An onboarded team isolates its data under bundleKey = its bindleId.
 *
 * A tiny subscriber registry lets React bind to changes via
 * `useSyncExternalStore` (see {@link useActiveTeam}); it is also invalidated by
 * cross-tab `storage` events so switching teams in one tab is reflected in
 * others.
 */

/** The persisted active-team selection. */
export interface ActiveTeam {
  /** The team's Bindle id. '' for the built-in current workspace. */
  bindleId: string;
  /** Human-readable team name shown in the gate and the top-bar switcher. */
  teamName: string;
  /**
   * The tenant bundle key stamped into every request. '' (empty) for the
   * built-in current workspace (DEFAULT bundle); the bindleId for onboarded
   * teams.
   */
  bundleKey: string;
}

const STORAGE_KEY = 'ev.activeTeam';

// Module-level cache. `undefined` means "not yet read from storage"; `null`
// means "read, and there is no active team".
let cache: ActiveTeam | null | undefined;

const listeners = new Set<() => void>();

function readFromStorage(): ActiveTeam | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ActiveTeam> | null;
    if (
      parsed &&
      typeof parsed.bindleId === 'string' &&
      typeof parsed.teamName === 'string' &&
      typeof parsed.bundleKey === 'string'
    ) {
      return {
        bindleId: parsed.bindleId,
        teamName: parsed.teamName,
        bundleKey: parsed.bundleKey,
      };
    }
    return null;
  } catch {
    return null;
  }
}

function emit(): void {
  listeners.forEach((l) => l());
}

/**
 * Synchronously returns the active team, or null when none is chosen. The
 * returned reference is stable until {@link setActiveTeam}/{@link clearActiveTeam}
 * (or a cross-tab change) so it is safe as a `useSyncExternalStore` snapshot.
 */
export function getActiveTeam(): ActiveTeam | null {
  if (cache === undefined) cache = readFromStorage();
  return cache;
}

/**
 * Synchronously returns the active team's tenant bundle key, or '' when no team
 * is chosen (DEFAULT). Read by {@link buildMetaInfo} on every request.
 */
export function getActiveTeamBundleKey(): string {
  return getActiveTeam()?.bundleKey ?? '';
}

/** Persists and activates the chosen team, notifying subscribers. */
export function setActiveTeam(team: ActiveTeam): void {
  cache = team;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(team));
  } catch {
    /* storage unavailable — in-memory selection still works */
  }
  emit();
}

/** Clears the active team (returns the app to the tenant gate). */
export function clearActiveTeam(): void {
  cache = null;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable */
  }
  emit();
}

/**
 * Subscribes to active-team changes. Returns an unsubscribe function. Used by
 * the React binding; pure-TS callers can use the synchronous getters directly.
 */
export function subscribeActiveTeam(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// Keep tabs in sync: another tab changing the selection invalidates our cache.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      cache = undefined;
      emit();
    }
  });
}
